import { API } from "../../../api/constants"
import { authenticatedAxiosInstance } from "../../../auth/AuthenticatedAxios"
import { getEmployeeNamesOnly } from "../../Mitarbeiter/api/apiCalls"
import { Tour, TourEmployeeRef, TourObjectRef, TourStop, TourWeekday } from "../types"

const emptyAddress = { street: "", nr: "", plz: "", city: "" }

const mapObject = (item: Record<string, unknown>): TourObjectRef => {
    const adress = (item.adress as TourObjectRef["adress"]) ?? emptyAddress
    const objectIndication = String(item.objectIndication ?? item.name ?? "")
    return {
        _id: String(item._id ?? ""),
        objectIndication,
        name: objectIndication,
        adress,
    }
}

const asObjectList = (payload: unknown): TourObjectRef[] => {
    const list = Array.isArray(payload) ? payload : payload && typeof payload === "object" && "docs" in payload ? (payload as { docs: unknown[] }).docs : []
    if (!Array.isArray(list)) return []
    return list.map((item) => mapObject(item as Record<string, unknown>))
}

const asEmployeeList = (payload: unknown): TourEmployeeRef[] => {
    if (Array.isArray(payload)) return payload as TourEmployeeRef[]
    if (payload && typeof payload === "object" && "docs" in payload && Array.isArray((payload as { docs: unknown }).docs)) {
        return (payload as { docs: TourEmployeeRef[] }).docs
    }
    return []
}

const asTourList = (payload: unknown): Tour[] => (Array.isArray(payload) ? (payload as Tour[]) : [])

export const getTourList = async (): Promise<Tour[]> =>
    authenticatedAxiosInstance.get(API + "/tours").then((res) => asTourList(res.data))

export const getTour = async (id: string): Promise<Tour | undefined> =>
    authenticatedAxiosInstance.get(API + `/tours/${id}`).then((res) => res.data as Tour)

export const createTour = (name: string) =>
    authenticatedAxiosInstance.post(API + "/tours", { name }).then((res) => res.data as Tour)

export const updateTour = (id: string, data: { name: string; active: boolean; employee?: string | null }) =>
    authenticatedAxiosInstance.put(API + `/tours/${id}`, data).then((res) => res.data as Tour)

export const deleteTour = (id: string) =>
    authenticatedAxiosInstance.delete(API + `/tours/${id}`).then((res) => res.data)

export const addStopToTour = (tourId: string, object: TourObjectRef, weekday: TourWeekday, extra?: Partial<TourStop>) =>
    authenticatedAxiosInstance
        .post(API + `/tours/${tourId}/stops`, {
            object: object._id,
            weekday,
            isAdHoc: extra?.isAdHoc === true,
            adHocTask: extra?.adHocTask,
        })
        .then((res) => res.data as Tour)

export const removeStopFromTour = (tourId: string, stopId: string) =>
    authenticatedAxiosInstance.delete(API + `/tours/${tourId}/stops/${stopId}`).then((res) => res.data as Tour)

export const moveStopOrder = (tourId: string, stopId: string, direction: -1 | 1) =>
    authenticatedAxiosInstance.patch(API + `/tours/${tourId}/stops/${stopId}/reorder`, { direction }).then((res) => res.data as Tour)

export const moveStopToWeekday = (tourId: string, stopId: string, weekday: TourWeekday) =>
    authenticatedAxiosInstance.patch(API + `/tours/${tourId}/stops/${stopId}/weekday`, { weekday }).then((res) => res.data as Tour)

export const moveStopToOtherTour = (fromTourId: string, toTourId: string, stopId: string, weekday: TourWeekday) =>
    authenticatedAxiosInstance.post(API + `/tours/${fromTourId}/stops/${stopId}/move`, { toTourId, weekday }).then((res) => res.data as Tour)

export const getObjectsForTourStops = async (): Promise<TourObjectRef[]> => {
    const payload = await authenticatedAxiosInstance.get(API + "/object/address").then((res) => res.data)
    return asObjectList(payload)
}

export const getEmployeesForTourAssignment = async (search?: string): Promise<TourEmployeeRef[]> => {
    const payload = await getEmployeeNamesOnly(search)
    return asEmployeeList(payload)
}
